import dayjs from "dayjs";
import { Grid2 as Grid, Typography, useTheme } from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import React, { useContext, useEffect } from "react";
import { MentionsInput } from "./MentionsInput";
import { useEmailActions } from "../../hooks/useEmailActions";
import { ActivityRender } from "./ActivityRender";
import LoadingPage from "../../components/LoadingPage";
import { ActivityModel } from "@attelas/shared";
import ActionWithAi from "../../components/ActionWithAi";
import AssignButton from "../../components/AssignButton";
import { useTasksEmail } from "../../hooks/useTasksEmail";
import { useScrollToComment } from "./useScrollToComment";
import { ACTIVITY_SCROLLER_ID } from "./const";
import { PageContext } from "../../context/PageContext";
import { useOutlook } from "../../hooks/useOutlook";
import { ContactAlert } from "./ContactAlert";
import { useParams } from "react-router-dom";

function Activities() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { emailId } = useParams<{ emailId: string }>();
  const { isInReadMode } = useOutlook();
  const showAlert = !emailId && isInReadMode;
  const { setNewTitle } = useContext(PageContext);
  const { tasks } = useTasksEmail();
  const { emailActions, isLoading } = useEmailActions();
  const sortedActivities =
    emailActions?.activities?.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) ?? [];

  useScrollToComment(sortedActivities.length > 0);

  useEffect(() => {
    setNewTitle("Activities");
  }, [setNewTitle]);

  const footer = (
    <Grid
      component="footer"
      container={true}
      width="100%"
      py={1}
      spacing={1}
      bgcolor={isDarkMode ? "#121212" : "common.white"}
    >
      <Grid size={12}>
        <MentionsInput />
      </Grid>
      <Grid size={6}>
        <ActionWithAi actions={tasks} relatedIds={[]} />
      </Grid>
      <Grid size={6}>
        <AssignButton />
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!sortedActivities.length) {
    return (
      <Grid container={true} flexDirection="column" flexWrap="nowrap" height="100%">
        {showAlert && <ContactAlert />}
        <Grid
          flex="1 1 auto"
          container={true}
          flexDirection="column"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="center"
        >
          <TelegramIcon color="primary" sx={{ fontSize: "60px" }} />
          <Typography>You're starting a new conversation.</Typography>
          <Typography>Begin chatting below.</Typography>
        </Grid>
        {footer}
      </Grid>
    );
  }

  return (
    <Grid container={true} flexDirection="column" flexWrap="nowrap" height="100%">
      {showAlert && <ContactAlert />}
      <Grid
        id={ACTIVITY_SCROLLER_ID}
        container={true}
        flexDirection="column"
        flexWrap="nowrap"
        height="100%"
        flex="1 1 auto"
        pb={1}
        sx={{ overflowY: "auto" }}
      >
        {sortedActivities.map((activity, index) => (
          <ActivityRender
            key={activity.id || activity.createdAt}
            activity={activity}
            leading={computeLeading(sortedActivities, index)}
          />
        ))}
      </Grid>
      {footer}
    </Grid>
  );
}

function computeLeading(sortedActivities: ActivityModel[], index: number): boolean {
  if (index === 0) {
    return true;
  }

  const previousActivity = sortedActivities[index - 1];
  const currentActivity = sortedActivities[index];

  if (previousActivity.createdBy?.id !== currentActivity.createdBy?.id) {
    return true;
  }

  return Math.abs(dayjs(currentActivity.createdAt).diff(dayjs(previousActivity.createdAt), "minutes")) > 0;
}

export default Activities;
